import { ArrowRight } from 'lucide-react'

import { Button, type ButtonVariant } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/CopyButton'
import { GoogleSignInButton } from '@/components/ui/google-sign-in-button'
import { Demo, DsSection } from '../primitives'

const VARIANTS: { variant: ButtonVariant; label: string }[] = [
  { variant: 'default', label: 'Get your free quotes' },
  { variant: 'outline', label: 'Call us' },
  { variant: 'ghost', label: 'Ghost' },
  { variant: 'link', label: 'Link' },
  { variant: 'destructive', label: 'Delete' },
]

function Usage({ value }: { value: string }) {
  return (
    <div className="text-ink-400 flex items-center gap-1 font-mono text-[10px]">
      <span>{value}</span>
      <CopyButton
        value={value}
        className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
      />
    </div>
  )
}

function VariantDemo({ variant, label }: { variant: ButtonVariant; label: string }) {
  return (
    <div className="flex flex-col items-start gap-2">
      <Button variant={variant}>{label}</Button>
      <Usage value={`variant="${variant}"`} />
    </div>
  )
}

export function ButtonsSection() {
  return (
    <DsSection
      id="buttons"
      title="Buttons"
      marketing
      intro="Button labels are sentence case — e.g. “Get your free quotes”, not “Get Your Free Quotes”."
    >
      <Demo label="Variants" className="flex flex-wrap items-start gap-6">
        {VARIANTS.map(({ variant, label }) => (
          <VariantDemo key={variant} variant={variant} label={label} />
        ))}
      </Demo>
      <Demo label="Sizes" className="flex flex-wrap items-center gap-4">
        <Button size="xs">Extra small</Button>
        <Button size="sm">Small</Button>
        <Button size="default">Default</Button>
        <Button size="lg">Large</Button>
        <Button size="xlg">
          Extra large <ArrowRight />
        </Button>
      </Demo>
      <Demo label="States" className="flex flex-wrap items-center gap-4">
        <Button disabled>Disabled</Button>
        <Button loading="Saving…">Save</Button>
        <Button variant="outline" loading="Loading…">
          Load
        </Button>
      </Demo>
      <Demo label="Google sign-in" className="flex max-w-sm flex-col items-start gap-6">
        <div className="flex flex-col items-start gap-2">
          <GoogleSignInButton />
          <Usage value="<GoogleSignInButton />" />
        </div>
        <div className="flex w-full flex-col items-start gap-2">
          <GoogleSignInButton className="w-full" />
          <Usage value='<GoogleSignInButton className="w-full" />' />
        </div>
      </Demo>
      <Demo label="On dark" dark className="flex flex-wrap items-center gap-4">
        <Button>Get your free quotes</Button>
        <Button variant="ghost" className="hover:text-ink text-white">
          Call us
        </Button>
      </Demo>
    </DsSection>
  )
}
